import React, { useState } from "react";
import Header from "../../layouts/header";
import Footer from "../../layouts/footer";
import { ErrorMessage, Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { locateCertificate } from "../../api/server";
import { FiSearch } from "react-icons/fi";
import "./LocateCertificates.css";

const validationSchema = Yup.object({
  firstname: Yup.string().trim().required("First name is required"),
  lastname: Yup.string().trim().required("Last name is required"),
  ssn: Yup.string().required("Last 4 digits of SSN are required").matches(/^\d{4}$/, "Enter exactly 4 digits"),
});

const CourseCertificate = ({ courseId, generated, downloaded, date }) => {
  if (!courseId) return <span className="locate-muted">Not available</span>;
  if (generated === "") return <span className="locate-pending">Pending</span>;
  const label = date ? date.split("T")[0] : "Certificate";
  return downloaded && downloaded !== "yes" ? (
    <a href={downloaded} target="_blank" rel="noopener noreferrer">{label}</a>
  ) : <span>{label}</span>;
};

const LocateCertificates = () => {
  const [response, setResponse] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");

  return (
    <div className="locate-certificates-page">
      <Header />
      <main className="locate-main">
        <div className="locate-heading">
          <span className="locate-eyebrow">COURSE CERTIFICATES</span>
          <h1>Locate <span>Certificates</span></h1>
          {/* <p>Find your course certificates using the information from your enrollment.</p> */}
        </div>

        <section className="locate-card" aria-label="Certificate search">
          <Formik
            initialValues={{ firstname: "", lastname: "", ssn: "" }}
            validationSchema={validationSchema}
            onSubmit={async (values) => {
              setSearchLoading(true);
              setSearchError("");
              setResponse([]);
              try {
                const res = await locateCertificate({ ...values, firstname: values.firstname.trim(), lastname: values.lastname.trim() });
                setResponse(res.data.data);
                if (res.data.data.length === 0) {
                  toast.error("No certificate found");
                }
              } catch (err) {
                const message = err.response?.data?.error || "We couldn't complete your search. Please try again.";
                setSearchError(message);
                toast.error(message);
              } finally {
                setSearchLoading(false);
              }
            }}
          >
            {({ errors, touched }) => (
              <Form className="locate-form" noValidate>
                {[
                  { name: "firstname", label: "First name", placeholder: "Enter first name", autoComplete: "given-name" },
                  { name: "lastname", label: "Last name", placeholder: "Enter last name", autoComplete: "family-name" },
                  { name: "ssn", label: "Last 4 digits of SSN", placeholder: "4 digits", autoComplete: "off", inputMode: "numeric", maxLength: 4 },
                ].map(({ name, label, ...props }) => (
                  <div className="locate-field" key={name}>
                    <label htmlFor={`locate-${name}`}>{label}</label>
                    <Field id={`locate-${name}`} name={name} type="text" {...props}
                      aria-invalid={Boolean(touched[name] && errors[name])}
                      aria-describedby={touched[name] && errors[name] ? `locate-${name}-error` : undefined} />
                    <ErrorMessage name={name} component="div" id={`locate-${name}-error`} className="locate-field-error" />
                  </div>
                ))}
                <button type="submit" className="locate-search-button" disabled={searchLoading}>
                  <FiSearch aria-hidden="true" />{searchLoading ? "Searching…" : "Search"}
                </button>
              </Form>
            )}
          </Formik>

          <div className="locate-results" aria-live="polite" aria-busy={searchLoading}>
            {searchLoading ? <p className="locate-state">Searching for your certificates…</p> : searchError ? (
              <div className="locate-state locate-error" role="alert"><h3>Search couldn't be completed</h3><p>{searchError}</p></div>
            ) : response.length === 0 ? (
              <div className="locate-state locate-note">
                <p><strong>No results found.</strong> If you feel that the course has been completed and the information is not showing up, please give us a call at <a href="tel:5035068813">503-506-8813</a> and we will help you right away.</p>
              </div>
            ) : (
              <div className="locate-results-content">
                <h3>Search results <span>({response.length})</span></h3>
                <div className="locate-table-wrap">
                  <table className="locate-table">
                    <caption className="visually-hidden">Matching clients and course certificates</caption>
                    <thead><tr><th scope="col">Client name</th><th scope="col">1st course</th><th scope="col">2nd course</th></tr></thead>
                    <tbody>{response.map((item, index) => (
                      <tr key={index}>
                        <th scope="row">{item.type === "Main" ? `${item.userFirstName} ${item.userLastName}` : `${item.jointFirstName} ${item.jointLastName}`}</th>
                        <td><CourseCertificate courseId={item.courseFirstId} generated={item.courseFirstGenerated} downloaded={item.courseFirstDownloaded} date={item.courseFirstDate} /></td>
                        <td><CourseCertificate courseId={item.courseSecondId} generated={item.generated} downloaded={item.downloaded} date={item.date} /></td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default LocateCertificates;
